import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ImportMatchWizard from './ImportMatchWizard';

interface ImportReportDataListItem {
  reportId: number;
  dataSource: string;
  seasonName: string;
  regionName?: string;
  consultantName?: string;
  businessName?: string;
  farmName?: string;
  fieldName?: string;
  productName?: string;
  productCategoryName?: string;
  geneStack?: string;
  rate?: number;
  unitName?: string;
  plantingDate?: string;
  matchStatus?: string;
  matchedRecordId?: string;
}

const chipSx = { height: 20, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } };

const ImportReportDataManagement: React.FC = () => {
  const auth = useAuth();
  const [rows, setRows] = useState<ImportReportDataListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [pageSize, setPageSize] = useState(50);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matchingRow, setMatchingRow] = useState<ImportReportDataListItem | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` });

  // Debounce the search box so we don't fire a request per keystroke.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(pageSize),
      });
      if (search) params.set('search', search);
      const response = await fetch(`/api/import-report-data?${params.toString()}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch imported report data');
      const data = await response.json();
      setRows(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, auth.tokens?.accessToken]);

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchRows();
    }
  }, [fetchRows, auth.isSignedIn]);

  const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString() : '-');

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <AssignmentIcon />
          Imported Report Data
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only staging data imported from the source CCA reporting system, awaiting matching to
        this app's Businesses/Farms/Fields. {totalCount.toLocaleString()} rows total.
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search by business, farm, field, consultant, product, season, or region..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No imported report data found.</Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.8125rem' },
                '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Report ID</TableCell>
                  <TableCell>Season</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Consultant</TableCell>
                  <TableCell>Business</TableCell>
                  <TableCell>Farm</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Planting Date</TableCell>
                  <TableCell>Match Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.reportId}
                    hover
                    onClick={() => setMatchingRow(row)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{row.reportId}</TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>
                      <Tooltip title={row.seasonName}>
                        <Typography variant="inherit" noWrap>{row.seasonName || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.regionName || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>
                      <Tooltip title={row.consultantName || ''}>
                        <Typography variant="inherit" noWrap>{row.consultantName || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Tooltip title={row.businessName || ''}>
                        <Typography variant="inherit" noWrap>{row.businessName || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.farmName || '-'}</TableCell>
                    <TableCell>{row.fieldName || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Tooltip title={row.productName || ''}>
                        <Typography variant="inherit" noWrap>{row.productName || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.rate != null ? `${row.rate}${row.unitName ? ` ${row.unitName}` : ''}` : '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.plantingDate)}</TableCell>
                    <TableCell>
                      {row.matchStatus ? (
                        <Chip label={row.matchStatus} size="small" color="primary" sx={chipSx} />
                      ) : (
                        <Chip label="Unmatched" size="small" color="default" sx={chipSx} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>
      )}

      {matchingRow && (
        <ImportMatchWizard
          row={matchingRow}
          open={!!matchingRow}
          onClose={() => setMatchingRow(null)}
          onMatched={fetchRows}
        />
      )}
    </Box>
  );
};

export default ImportReportDataManagement;
